/**
 * Cell renderers + sort comparators for List view columns.
 *
 * The gateway's Card row (@chaos/types/kanban) is leaner than the
 * Twenty-era CardSnapshot — it does not yet ship status/members/
 * progress aggregates. For Phase 12 we render the fields we have and
 * leave placeholders ("—") for the rest, so the component renders
 * cleanly today and the columns light up once the gateway extends
 * the wire shape (no UI rewrite needed).
 */
import type { Card } from '@chaos/types/kanban';
import { CalendarClock, Users } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../../../lib/utils';
import type { CardListColumnId } from './list-types';

const AUTONOMY_LABEL: Record<NonNullable<Card['autonomyLevel']>, string> = {
  HITL: 'HITL',
  HOTL: 'HOTL',
  HOOL: 'HOOL',
  agent_only: 'Agent only',
};

export interface ListColumnDef {
  id: CardListColumnId;
  label: string;
  width: number;
  cell: (card: Card) => React.ReactNode;
  compare: (a: Card, b: Card) => number;
  flex?: boolean;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export const COLUMNS: Record<CardListColumnId, ListColumnDef> = {
  title: {
    id: 'title',
    label: 'Title',
    width: 320,
    flex: true,
    cell: (card) => (
      <span className="truncate text-sm font-medium text-foreground">{card.title}</span>
    ),
    compare: (a, b) => a.title.localeCompare(b.title),
  },
  status: {
    id: 'status',
    label: 'Status',
    width: 132,
    cell: (card) => (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        {card.archivedAt ? 'archived' : 'in progress'}
      </span>
    ),
    compare: (a, b) => Number(Boolean(a.archivedAt)) - Number(Boolean(b.archivedAt)),
  },
  agent: {
    id: 'agent',
    label: 'Agent',
    width: 180,
    cell: (card) =>
      card.primaryAgentId ? (
        <span className="truncate text-xs text-foreground" title={card.primaryAgentId}>
          {card.primaryAgentId.slice(0, 8)}…
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    compare: (a, b) => (a.primaryAgentId ?? '').localeCompare(b.primaryAgentId ?? ''),
  },
  autonomy: {
    id: 'autonomy',
    label: 'Autonomy',
    width: 110,
    cell: (card) =>
      card.autonomyLevel ? (
        <span className="inline-flex items-center rounded border border-border bg-background px-1.5 py-0.5 text-2xs uppercase tracking-wide text-foreground">
          {AUTONOMY_LABEL[card.autonomyLevel]}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    compare: (a, b) => (a.autonomyLevel ?? '').localeCompare(b.autonomyLevel ?? ''),
  },
  progress: {
    id: 'progress',
    label: 'Progress',
    width: 120,
    cell: () => <span className="text-xs text-muted-foreground">—</span>,
    compare: () => 0,
  },
  due: {
    id: 'due',
    label: 'Due',
    width: 130,
    cell: (card) =>
      card.dueDate ? (
        <span className="inline-flex items-center gap-1 text-xs text-foreground">
          <CalendarClock size={11} className="text-muted-foreground" aria-hidden />
          {new Date(card.dueDate).toLocaleDateString()}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    compare: (a, b) => {
      const av = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
      const bv = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
      return av - bv;
    },
  },
  last_activity: {
    id: 'last_activity',
    label: 'Last activity',
    width: 130,
    cell: (card) => (
      <span className="text-xs text-muted-foreground">{formatRelative(card.updatedAt)}</span>
    ),
    compare: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
  },
  priority: {
    id: 'priority',
    label: 'Priority',
    width: 100,
    cell: () => <span className="text-xs text-muted-foreground">—</span>,
    compare: () => 0,
  },
  members: {
    id: 'members',
    label: 'Members',
    width: 90,
    cell: (card) => (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Users size={11} aria-hidden /> {card.primaryAgentId ? 1 : 0}
      </span>
    ),
    compare: (a, b) => Number(Boolean(a.primaryAgentId)) - Number(Boolean(b.primaryAgentId)),
  },
  cost_today: {
    id: 'cost_today',
    label: 'Cost today',
    width: 110,
    cell: () => <span className="text-xs text-muted-foreground">—</span>,
    compare: () => 0,
  },
};

export function visibleColumnDefs(ids: readonly CardListColumnId[]): ListColumnDef[] {
  return ids.map((id) => COLUMNS[id]);
}

// Tiny helper used by the row layout — kept exported so tests can
// reach in and assert row widths without re-implementing the math.
export function rowTemplateColumns(defs: readonly ListColumnDef[]): string {
  return defs.map((d) => (d.flex ? `minmax(${d.width}px, 1fr)` : `${d.width}px`)).join(' ');
}

export function totalRowWidth(defs: readonly ListColumnDef[]): number {
  return defs.reduce((acc, d) => acc + d.width, 0);
}

export const STATIC_ROW_HEIGHT = 36;
export const HEADER_ROW_HEIGHT = 36;

export const cnHelper = cn; // re-export so callers don't need a second import
